import React from 'react';
import { usePlaidIncomeWorkflow } from '../../../hooks/usePlaidIncomeWorkflow';

type Props = {
  trn_id?: string | null;
  type: 'bank' | 'payroll';
  label?: React.ReactNode;
  subtitle?: React.ReactNode;
  asButton?: boolean;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  onLinkClicked?: () => void;
  onPlaidSuccess?: () => void;
  onPlaidExit?: (completed: boolean) => void;
  onDone?: () => void;
  linkTokenData?: Record<string, any>;
};

const defaults = {
  bank: {
    label: 'Verify Income through bank deposits',
    subtitle:
      '(This may require you to provide a third party, Plaid, with your bank login credentials.)',
  },
  payroll: {
    label: 'Verify Income through payroll providers',
    subtitle:
      '(You may be redirected to Plaid to connect to your payroll provider.)',
  },
} as const;

const PlaidIncomeLink: React.FC<Props> = ({
  trn_id,
  type,
  label,
  subtitle,
  asButton = false,
  className,
  style,
  disabled = false,
  onLinkClicked,
  onPlaidSuccess,
  onPlaidExit,
  onDone,
  linkTokenData,
}) => {
  const { state, start, disabled: workflowDisabled } = usePlaidIncomeWorkflow({
    type,
    trn_id,
    onDone,
    onPlaidSuccess,
    onBeforeOpen: onLinkClicked,
    onExit: onPlaidExit,
    linkTokenData,
  });

  if (!trn_id) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled || workflowDisabled) return;
    start();
  };

  const content = (
    <>
      {label ?? defaults[type].label}
      {subtitle !== '' && (
        <div style={{ fontSize: '0.8125rem', color: '#444', lineHeight: 1.3 }}>
          {subtitle ?? defaults[type].subtitle}
        </div>
      )}
    </>
  );

  const actionDisabled = disabled || workflowDisabled;

  return (
    asButton ? (
      <button
        type="button"
        onClick={handleClick}
        disabled={actionDisabled}
        className={className ?? "btn-link text-decoration-none"}
        style={style}
      >
        {content}
      </button>
    ) : (
      <a
        href="#"
        onClick={handleClick}
        className={className}
        style={style}
        aria-disabled={actionDisabled}
      >
        {content}
      </a>
    )
  );
};

export default PlaidIncomeLink;
